import { useEffect, useState } from "react";
import type { PonudbaKategorija } from "@/mocks/ponudbaMeni";

interface MenuQuickNavProps {
  kategorije: PonudbaKategorija[];
}

/**
 * Sticky chip navigation that jumps to a category and highlights the one in view.
 */
export default function MenuQuickNav({ kategorije }: MenuQuickNavProps) {
  const [aktivna, setAktivna] = useState<string>("");

  useEffect(() => {
    const elementi = kategorije
      .map((kategorija) => document.getElementById(kategorija.id))
      .filter((element): element is HTMLElement => Boolean(element));

    if (!elementi.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const vidne = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (vidne[0]) setAktivna(vidne[0].target.id);
      },
      { rootMargin: "-140px 0px -55% 0px", threshold: [0, 0.2, 0.5] },
    );

    elementi.forEach((element) => observer.observe(element));
    if (elementi[0]) setAktivna(elementi[0].id);

    return () => observer.disconnect();
  }, [kategorije]);

  return (
    <div className="sticky top-[76px] z-30 -mx-1 mb-5 bg-background-50/95 px-1 pb-3 pt-3 backdrop-blur-sm md:top-[84px]">
      <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
        {kategorije.map((kategorija) => {
          const jeAktivna = aktivna === kategorija.id;

          return (
            <a
              key={kategorija.id}
              href={`#${kategorija.id}`}
              className={`flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 font-label text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                jeAktivna
                  ? "border-secondary-500 bg-secondary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-400 hover:text-accent-800"
              }`}
            >
              <span
                className={`font-label text-[9px] tracking-[0.1em] ${
                  jeAktivna ? "text-background-100/75" : "text-foreground-400"
                }`}
              >
                {kategorija.stevilka}
              </span>
              {kategorija.naslov}
            </a>
          );
        })}
      </div>
    </div>
  );
}